import { createContext, useState, useContext, ReactNode, useMemo, useCallback } from "react";
import { BreadcrumbItemData, BreadcrumbContextValue, BreadcrumbStackEntry } from "./types";

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

interface BreadcrumbsProviderProps {
  children: ReactNode;
}

/**
 * Module-level persistent stack.
 * React StrictMode (and certain route transitions) can unmount/remount the
 * provider, which resets useState.  Keeping the authoritative copy here
 * ensures the breadcrumb trail survives those remounts.
 */
let persistedStack: BreadcrumbStackEntry[] = [];

/**
 * Core push logic: given a stack and a new entry, returns the updated stack.
 * Rules:
 * 1. If top entry has same pathname → update label only (label change or StrictMode)
 * 2. If entry.pathname exists in stack at index N → truncate to N, update label
 * 3. Else → push new entry
 *
 * Rule 1 must come before rule 2 so that label updates (e.g. "Loading..." → actual title)
 * don't accidentally truncate to a defaultParent with the same pathname.
 */
const applyPush = (
  stack: BreadcrumbStackEntry[],
  entry: BreadcrumbStackEntry
): BreadcrumbStackEntry[] => {
  if (stack.length > 0) {
    const top = stack[stack.length - 1];

    // 1. Same pathname as top → update label only (label change or StrictMode double-mount)
    if (top.pathname === entry.pathname) {
      const updated = [...stack];
      updated[updated.length - 1] = { ...top, label: entry.label };
      return updated;
    }
  }

  // 2. Truncate if pathname already in stack
  const existingIndex = stack.findIndex(e => e.pathname === entry.pathname);
  if (existingIndex !== -1) {
    const truncated = stack.slice(0, existingIndex + 1);
    truncated[existingIndex] = { ...truncated[existingIndex], label: entry.label };
    return truncated;
  }

  // 3. Push new entry
  return [...stack, entry];
};

export const BreadcrumbsProvider = ({ children }: BreadcrumbsProviderProps) => {
  // Initialize from persisted stack so remounts don't lose state
  const [stack, _setStack] = useState<BreadcrumbStackEntry[]>(() => persistedStack);

  // Wrapper that keeps the module-level persisted stack in sync
  const setStack = useCallback(
    (
      update: BreadcrumbStackEntry[] | ((prev: BreadcrumbStackEntry[]) => BreadcrumbStackEntry[])
    ) => {
      _setStack(prev => {
        const next = typeof update === "function" ? update(prev) : update;
        persistedStack = next;
        return next;
      });
    },
    []
  );

  const pushBreadcrumb = useCallback(
    (entry: BreadcrumbStackEntry, defaultParents?: BreadcrumbStackEntry[], truncateTo?: string) => {
      setStack(prev => {
        // When stack is empty and we have defaultParents, build the full trail directly.
        if (prev.length === 0 && defaultParents?.length) {
          return [...defaultParents, entry];
        }

        let stack = prev;

        // Truncate to a parent pathname before pushing (used by tab pages)
        if (truncateTo) {
          const idx = stack.findIndex(e => e.pathname === truncateTo);
          if (idx >= 0) {
            stack = stack.slice(0, idx + 1);
          }
        }

        return applyPush(stack, entry);
      });
    },
    [setStack]
  );

  const resetStack = useCallback(() => {
    setStack([]);
  }, [setStack]);

  // Legacy setItems: converts BreadcrumbItemData[] to stack entries
  const setItems = useCallback(
    (items: BreadcrumbItemData[]) => {
      const entries: BreadcrumbStackEntry[] = items.map(item => ({
        pathname: item.href ?? "",
        label: item.label,
      }));
      setStack(entries);
    },
    [setStack]
  );

  // Legacy push: appends a BreadcrumbItemData to the stack
  const push = useCallback(
    (item: BreadcrumbItemData) => {
      setStack(prev => [...prev, { pathname: item.href ?? "", label: item.label }]);
    },
    [setStack]
  );

  // Derive BreadcrumbItemData[] from stack for backward compatibility
  const items = useMemo<BreadcrumbItemData[]>(() => {
    return stack.map((entry, index) => {
      const isLast = index === stack.length - 1;
      return {
        label: entry.label,
        href: isLast || entry.noLink ? undefined : entry.pathname || undefined,
      };
    });
  }, [stack]);

  const clear = useCallback(() => {
    setStack([]);
  }, [setStack]);

  const value = useMemo(
    () => ({ items, setItems, push, pushBreadcrumb, resetStack, clear }),
    [items, setItems, push, pushBreadcrumb, resetStack, clear]
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

/** Reset persisted stack — for tests only */
export const _resetPersistedStack = () => {
  persistedStack = [];
};

export const useBreadcrumbContext = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumbContext must be used within BreadcrumbsProvider");
  }
  return context;
};
